import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

interface Deal {
  kind: string;
  percent: string | null;
  salaryMinor: number | null;
  overtimeRateMinor: number | null;
  socialInsurancePercent: string | null;
  hiredOn: string | null;
}

/** The three the board draws; any other stored kind shows as itself. */
const CHIPS: Array<{ kind: string; label: string }> = [
  { kind: 'percent', label: 'share' },
  { kind: 'salary', label: 'salary' },
  { kind: 'none', label: 'none' },
];

const unwrap = <T,>(res: { data?: unknown }): T => {
  const body = res.data as { data?: T } | T | undefined;
  return ((body as { data?: T })?.data ?? body) as T;
};

const inputStyle = {
  width: '100%', padding: '6px 8px', marginTop: 4, font: 'inherit',
  borderRadius: 'var(--r-md)', border: '1px solid var(--stroke-200)',
} as const;

/**
 * THE DEAL EDITOR on a doctor's compensation card (g_c3): share / salary /
 * none chips, then the amounts the deal takes. PUT /payroll/deals/:personId.
 */
export function DealEditor({ personId }: { personId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['payroll', 'deal', personId],
    queryFn: async () => unwrap<Deal | null>(await apiClient.get(`/payroll/deals/${personId}`)),
  });
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('percent');
  const [percent, setPercent] = useState('');
  const [salary, setSalary] = useState('');
  const [overtime, setOvertime] = useState('');
  const [insurance, setInsurance] = useState('');
  const [hiredOn, setHiredOn] = useState('');

  useEffect(() => {
    const d = q.data;
    setKind(d?.kind ?? 'percent');
    setPercent(d?.percent ?? '');
    setSalary(d?.salaryMinor != null ? String(d.salaryMinor / 100) : '');
    setOvertime(d?.overtimeRateMinor != null ? String(d.overtimeRateMinor / 100) : '');
    setInsurance(d?.socialInsurancePercent ?? '');
    setHiredOn(d?.hiredOn ? d.hiredOn.slice(0, 10) : '');
  }, [q.data, personId]);

  const save = useMutation({
    mutationFn: async () => {
      const num = (s: string) => (s.trim() === '' ? undefined : Number(s));
      const minor = (s: string) => {
        const n = num(s);
        return n === undefined ? undefined : Math.round(n * 100);
      };
      const salaried = kind === 'salary' || kind === 'hybrid';
      const body: Record<string, unknown> = { kind };
      if (kind === 'percent' || kind === 'hybrid') body.percent = num(percent);
      if (salaried) {
        body.salaryMinor = minor(salary);
        body.overtimeRateMinor = minor(overtime);
        body.socialInsurancePercent = num(insurance);
      }
      if (hiredOn) body.hiredOn = hiredOn;
      await apiClient.put(`/payroll/deals/${personId}`, body);
    },
    onSuccess: () => {
      toast.success('Deal saved — this month’s run uses it.');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['payroll'] });
    },
    onError: () => toast.error('That did not save — nothing was changed.'),
  });

  const chips = CHIPS.some((c) => c.kind === kind) ? CHIPS : [...CHIPS, { kind, label: kind.replace('_', ' ') }];
  const salaried = kind === 'salary' || kind === 'hybrid';
  const invalid =
    (kind === 'percent' && percent.trim() === '') || (salaried && salary.trim() === '');

  return (
    <div className="mt8">
      <p className="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
        {chips.map((c) => (
          <button key={c.kind} type="button"
            className={`chip chip-sm ${kind === c.kind ? 'chip-soft' : 'chip-act chip-outline'}`}
            aria-pressed={kind === c.kind}
            onClick={() => { setKind(c.kind); setOpen(true); }}>
            {c.kind === 'percent' && percent ? `${percent}% share` : c.label}
          </button>
        ))}
      </p>
      {open ? (
        <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
          {kind === 'none' ? (
            <p className="t-sec mut">No salary and no share — nothing is owed to them.</p>
          ) : null}
          {kind === 'percent' || kind === 'hybrid' ? (
            <label className="t-cap mut">Share of collected (%)
              <input type="number" min={0} max={100} step="0.5" value={percent}
                onChange={(e) => setPercent(e.target.value)} style={inputStyle} />
            </label>
          ) : null}
          {salaried ? (
            <>
              <label className="t-cap mut">Monthly salary (EGP)
                <input type="number" min={0} value={salary}
                  onChange={(e) => setSalary(e.target.value)} style={inputStyle} />
              </label>
              <label className="t-cap mut">Overtime rate per hour (EGP)
                <input type="number" min={0} value={overtime}
                  onChange={(e) => setOvertime(e.target.value)} style={inputStyle} />
              </label>
              <label className="t-cap mut">Social insurance (%)
                <input type="number" min={0} max={100} step="0.5" value={insurance}
                  onChange={(e) => setInsurance(e.target.value)} style={inputStyle} />
              </label>
            </>
          ) : null}
          <label className="t-cap mut">Hired on
            <input type="date" value={hiredOn}
              onChange={(e) => setHiredOn(e.target.value)} style={inputStyle} />
          </label>
          <p className="flex" style={{ gap: 8 }}>
            <button type="button" className="btn btn-outline"
              disabled={save.isPending || invalid} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save deal'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
          </p>
        </div>
      ) : null}
    </div>
  );
}
